import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, MessageSquare, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { fetchApi, formatDate, formatTime } from '../lib/utils';

export default function Messages() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState({ subject: '', content: '' });

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const data = await fetchApi('/messages');
      setMessages(data);
      // Mark all as read
      for (const msg of data.filter(m => !m.read)) {
        await fetchApi(`/messages/${msg.id}/read`, { method: 'PUT' });
      }
    } catch (error) {
      console.error('Load messages error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.subject.trim() || !newMessage.content.trim()) return;
    
    setSending(true);
    try {
      await fetchApi(`/messages?subject=${encodeURIComponent(newMessage.subject)}&content=${encodeURIComponent(newMessage.content)}`, {
        method: 'POST',
      });
      setNewMessage({ subject: '', content: '' });
      loadMessages();
    } catch (error) {
      console.error('Send error:', error);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pb-8">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <h1 className="text-lg font-bold">Messages</h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* New Message Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-default mb-8"
        >
          <h2 className="font-bold mb-4">Contact Support</h2>
          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <Input
                value={newMessage.subject}
                onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                placeholder="Subject"
                className="bg-zinc-900 border-zinc-800"
                data-testid="subject-input"
                required
              />
            </div>
            <div>
              <Textarea
                value={newMessage.content}
                onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                placeholder="How can we help you?"
                className="bg-zinc-900 border-zinc-800 min-h-[120px]"
                data-testid="content-input"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={sending}
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full"
              data-testid="send-message-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Message
            </Button>
          </form>
        </motion.div>

        {/* Message History */}
        <h2 className="font-bold mb-4">Message History</h2>
        {messages.length > 0 ? (
          <div className="space-y-4">
            {messages.map((message, index) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`message-item ${message.from_admin ? 'admin' : 'user'}`}
                data-testid={`message-${index}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-bold ${message.from_admin ? 'text-[#CCFF00]' : 'text-zinc-400'}`}>
                    {message.from_admin ? 'VYTA Support' : 'You'}
                  </span>
                  <span className="text-xs text-zinc-500">
                    {formatDate(message.created_at)} at {formatTime(message.created_at)}
                  </span>
                  {message.read && !message.from_admin && (
                    <Check className="w-3 h-3 text-zinc-500" />
                  )}
                </div>
                <p className="font-medium mb-1">{message.subject}</p>
                <p className="text-sm text-zinc-300">{message.content}</p>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <MessageSquare className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No Messages</h3>
            <p className="text-zinc-400">Your conversation history will appear here</p>
          </div>
        )}
      </main>
    </div>
  );
}
